import { useCallback, useMemo } from "react";
import { CategorizationWithConfig } from "../../configMap/interfaces/category";
import useKeyEvent from "../../configMap/hooks/useKeyEvent";
import { Keys } from "../../common/constants";
import { GetRef, Select } from "antd";

type SelectRef = GetRef<typeof Select>;

interface SharedCategorizationData {
  handleFocus: () => void;
  handleSearch: (value: string) => void;
  isIncomplete: boolean;
  isCategorizationDisabledByParent: boolean | undefined;
}

/**
 * Shared hook for common categorization cell logic used by both Dynamic and Fixed types
 */
const useCategorizationCell = (
  categorizationWithConfig: CategorizationWithConfig,
  categorizationData: SharedCategorizationData,
  selectRef: React.RefObject<SelectRef | null>,
  disabled: boolean,
) => {
  const handleKeyEvent = useKeyEvent(Keys.ESCAPE, () =>
    selectRef.current?.blur(),
  );

  const searchOnFocus = useMemo(
    () => categorizationWithConfig.categorization.search?.fetchOnFocus,
    [categorizationWithConfig.categorization.search?.fetchOnFocus],
  );

  const onFocus = useCallback(() => {
    categorizationData.handleFocus();

    if (searchOnFocus) {
      categorizationData.handleSearch("");
    }
  }, [categorizationData, searchOnFocus]);

  const className = useMemo(() => {
    if (disabled) {
      return "categorization-item__select-disabled";
    }

    let classes = "";
    if (categorizationData.isIncomplete) {
      classes += "categorization-item__select-is-incomplete";
    }
    if (categorizationData.isCategorizationDisabledByParent) {
      classes += " categorization-item__select-no-parent-value";
    }

    return classes;
  }, [
    disabled,
    categorizationData.isIncomplete,
    categorizationData.isCategorizationDisabledByParent,
  ]);

  return {
    handleKeyEvent,
    onFocus,
    className,
  };
};

export default useCategorizationCell;
